/**
 * Global page-jump hotkeys (B/P/E/C/Y/H) and contextual T = Today.
 *
 * E always goes to Timeline. T (and 0) snap Timeline/Calendar back to the
 * current time window without changing view/scale/range.
 */

export function pagePathname(url: string | undefined | null): string {
  if (!url) return '';
  const withoutQuery = url.split('?')[0] ?? '';
  if (withoutQuery.startsWith('http')) {
    try {
      return new URL(withoutQuery).pathname;
    } catch {
      return withoutQuery;
    }
  }
  return withoutQuery;
}

export function isTimelinePath(url: string | undefined | null): boolean {
  const path = pagePathname(url);
  return (
    path === '/dashboard' ||
    path.startsWith('/month/') ||
    path.startsWith('/week/') ||
    path.startsWith('/year/')
  );
}

export function isCalendarPath(url: string | undefined | null): boolean {
  const path = pagePathname(url);
  return path === '/events_calendar' || path.startsWith('/events_calendar/');
}

/** Pages where T/0 reset the current time window (Today). */
export function isTimeNavigablePath(url: string | undefined | null): boolean {
  return isTimelinePath(url) || isCalendarPath(url);
}

export function isTodayHotkey(key: string): boolean {
  return key === '0' || key.toLowerCase() === 't';
}

interface TodayHotkeyEvent {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

/**
 * True while a dialog is showing. Closed MobileSheets (and the ActionSheets built
 * on them) stay mounted as inert, aria-hidden dialogs, so those don't count.
 */
export function isDialogOpen(
  root: { querySelector: (selector: string) => unknown } = document,
): boolean {
  return !!root.querySelector('[role="dialog"]:not([inert]):not([aria-hidden="true"])');
}

/** True when T/0 should snap to today — not with modifiers or an open dialog. */
export function shouldHandleTodayHotkey(
  event: TodayHotkeyEvent,
  options: { dialogOpen?: boolean } = {},
): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (options.dialogOpen) return false;
  return isTodayHotkey(event.key);
}

export function getGlobalPageDestination(key: string, timelinePath: string): string | undefined {
  switch (key.toLowerCase()) {
    case 'b':
      return '/boards';
    case 'p':
      return '/projects';
    case 'e':
      return timelinePath;
    case 'c':
      return '/events_calendar';
    case 'y':
      return '/cycles';
    case 'h':
      return '/habits';
    default:
      return undefined;
  }
}
